import { useDoctolibReleve } from '@fidalia/api';
import { Alert, Button, Card, Input, PageHeader, Spinner, TabPanel, Tabs } from '@fidalia/ui';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { MdLabel, MdLinkOff, MdPerson, MdSearch, MdSync, MdTv } from 'react-icons/md';

import logoDoctolib from '@/assets/doctolib-logomark.png';
import { CarteConnexion } from '@/features/doctolib/carte-connexion';
import {
  CarteExtensionRequise,
  ModalExtensionRequise,
} from '@/features/doctolib/extension-requise';
import { praticiensDepuisAgendas } from '@/features/doctolib/praticiens';
import { ListeMotifs, TablePraticiens } from '@/features/doctolib/tableaux';
import { useSynchroniserDoctolib } from '@/features/doctolib/use-synchroniser';
import { errorMessage } from '@/lib/auth';

export const Route = createFileRoute('/_app/doctolib')({
  component: DoctolibPage,
});

type Onglet = 'praticiens' | 'motifs';

/**
 * Ce que Fidalia a relevé dans l'espace pro Doctolib du cabinet.
 *
 * Écran de consultation, pas de configuration : rien ne s'y modifie, tout
 * vient de Doctolib. D'où la hiérarchie — l'état de la connexion en tête,
 * puis les inventaires, et une seule action, relancer le relevé.
 *
 * Ce relevé est écrit par l'extension navigateur, jamais par l'app : le compte
 * Doctolib vit dans le navigateur du praticien. Tant qu'aucune synchronisation
 * n'a eu lieu, la page n'a rien à montrer et le dit.
 */
function DoctolibPage() {
  const { data, isPending, error } = useDoctolibReleve();
  const sync = useSynchroniserDoctolib();

  const [onglet, setOnglet] = useState<Onglet>('praticiens');
  const [recherche, setRecherche] = useState('');

  const releve = data?.releve ?? null;
  const agendas = useMemo(() => releve?.agendas ?? [], [releve]);
  const tousMotifs = useMemo(() => releve?.motifs ?? [], [releve]);

  const motifs = useMemo(() => {
    const terme = recherche.trim().toLowerCase();
    if (!terme) return tousMotifs;
    return tousMotifs.filter((motif) => motif.nom.toLowerCase().includes(terme));
  }, [recherche, tousMotifs]);

  // Déduits des agendas : Doctolib ne renvoie aucune liste de praticiens.
  const praticiens = useMemo(
    () => praticiensDepuisAgendas(agendas, tousMotifs),
    [agendas, tousMotifs],
  );

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        icon={<MdTv />}
        title="Doctolib"
        description="Les agendas et motifs de consultation relevés dans votre espace pro."
      />

      {error ? <Alert tone="danger">{errorMessage(error, 'Chargement impossible.')}</Alert> : null}

      {isPending ? (
        <div className="flex justify-center py-16 text-content-subtle">
          <Spinner className="size-6" />
        </div>
      ) : releve === null ? (
        <PremiereSynchronisation sync={sync} />
      ) : (
        <>
          <CarteConnexion releve={releve} sync={sync} />

          <div className="flex flex-col gap-4">
            <Tabs
              id="doctolib"
              aria-label="Éléments relevés"
              value={onglet}
              onChange={setOnglet}
              items={[
                {
                  value: 'praticiens',
                  label: 'Praticiens',
                  icon: <MdPerson />,
                  badge: praticiens.length,
                },
                {
                  value: 'motifs',
                  label: 'Motifs',
                  icon: <MdLabel />,
                  badge: tousMotifs.length,
                },
              ]}
            />

            <TabPanel id="doctolib" value="praticiens" active={onglet}>
              <TablePraticiens praticiens={praticiens} agendas={agendas} motifs={tousMotifs} />
            </TabPanel>

            <TabPanel id="doctolib" value="motifs" active={onglet} className="flex flex-col gap-4">
              {/* Quarante motifs aux libellés proches : sans recherche, retrouver
                  « Entente financière Dr … » demande de parcourir la liste entière. */}
              <Input
                type="search"
                icon={<MdSearch />}
                placeholder="Rechercher un motif"
                aria-label="Rechercher un motif"
                value={recherche}
                onChange={(event) => setRecherche(event.target.value)}
                containerClassName="sm:max-w-sm"
              />

              <ListeMotifs motifs={motifs} categories={releve.categories} agendas={agendas} />
            </TabPanel>
          </div>
        </>
      )}

      {/* Une seule boîte pour toute la page : n'importe quel bouton de
          synchronisation la déclenche, l'explication est la même. */}
      <ModalExtensionRequise open={sync.rappelExtension} onClose={sync.fermerRappel} />
    </div>
  );
}

/**
 * Cabinet qui n'a jamais synchronisé.
 *
 * Pas un message d'erreur : c'est l'état normal au premier passage. L'écran
 * explique donc ce qui va être lu — un compte professionnel tiers est en jeu —
 * puis propose la seule action qui le résout, en primaire plein.
 *
 * Sans extension, l'obstacle n'est pas le même et la marche à suivre non plus :
 * un bouton « Synchroniser » n'aurait personne à qui parler. D'où deux écrans
 * distincts plutôt qu'un bouton grisé — l'un mène à l'installation, l'autre à
 * la synchronisation.
 */
function PremiereSynchronisation({ sync }: { sync: ReturnType<typeof useSynchroniserDoctolib> }) {
  if (!sync.disponible) return <CarteExtensionRequise />;

  return (
    <Card className="flex flex-col items-center gap-6 px-6 py-14 text-center">
      <PastilleDeliee />

      <div className="flex max-w-2xl flex-col items-center gap-2">
        <p className="text-xl font-semibold text-content">Synchroniser Fidalia avec Doctolib</p>
        {/* `max-w-2xl` et non `max-w-md` : sur trois lignes, la phrase se lit
            comme un paragraphe d'avertissement. Sur deux, elle reste une
            légende sous la scène. */}
        <p className="text-sm leading-relaxed text-content-muted">
          L’extension Fidalia lit vos agendas et motifs de consultation dans votre espace pro, pour
          que vos messages se règlent agenda par agenda et motif par motif. Aucune donnée patient
          n’est lue.
        </p>
      </div>

      <Button
        size="lg"
        icon={<MdSync className="size-5" />}
        loading={sync.enAttente}
        onClick={sync.demander}
      >
        Synchroniser Doctolib
      </Button>
    </Card>
  );
}

/**
 * Le logotype Doctolib, éteint, marqué d'un maillon brisé.
 *
 * Une icône générique ne dirait pas de quoi il s'agit : ce qui manque n'est
 * pas « une donnée », c'est le lien avec un logiciel précis. La marque le
 * nomme d'un coup d'œil, le badge dit son état.
 *
 * Désaturé et non retiré : en couleur, le logotype aurait l'air actif. En
 * gris, on le reconnaît sans le croire branché — et c'est précisément ce que
 * l'écran a à dire.
 *
 * Fidalia n'apparaît pas : on est déjà chez Fidalia, la seule inconnue est
 * l'autre bout du lien.
 */
function PastilleDeliee() {
  return (
    <span aria-hidden="true" className="relative">
      <span className="grid size-24 place-items-center rounded-full border border-border-subtle bg-surface-muted">
        <img src={logoDoctolib} alt="" className="h-10 w-auto opacity-40 grayscale" />
      </span>

      {/* Le liseré reprend le fond de la carte : il détoure le badge du cercle
          plutôt que de l'y poser à plat. Gris et non rouge — rien n'est en
          panne, le lien n'a simplement jamais été fait. */}
      <span className="absolute -bottom-0.5 -right-0.5 grid size-9 place-items-center rounded-full border-2 border-surface bg-content-subtle text-white">
        <MdLinkOff className="size-5" />
      </span>
    </span>
  );
}
